// types
import { ApolloClient } from '@apollo/client'
import { IRiskFieldValue } from 'types/risk'
import {
  IField,
  FieldType,
  IRiskView,
  FieldSource,
  IFieldOption,
  IRiskTemplate,
  IFieldValueMapItem
} from 'types/graphql'
// utils
import { safeGetOr } from 'utils/fp'
import { getOptionsBySource } from 'components/PageComponents/RiskTemplates/Edit/components/RiskTemplateFields/components/Lookup/utils'
import { find, pipe, mapValues, groupBy, sortBy as sortByFp } from 'lodash/fp'
import { getFieldsOptions, getOptionByValue, prepareTagValue } from 'utils/form/index'
import { fromPairs, get, isArray, isEmpty, isNil, omit, pick, sortBy } from 'lodash'
// constants
import { FORM_SELECT_SECURITY_OPTIONS } from 'constants/security'
import { DATE_OPTIONS, DATE_SPECIAL_TYPES } from 'components/FormElements/DatePicker/components/CustomToolbar'

const findFieldOption = (fieldValue: IRiskFieldValue, options: IFieldOption[], fieldType: FieldType) =>
  find<IFieldOption & { relatedFieldId?: string; sourceFieldId?: string }>((option) => {
    if (fieldType === FieldType.PickList) {
      return option.id === fieldValue?.value
    }
    if (fieldType === FieldType.Lookup && fieldValue?.sourceFieldId && option.sourceFieldId) {
      return option.value === fieldValue.value && option.sourceFieldId === fieldValue.sourceFieldId
    }
    if (fieldType === FieldType.Lookup && fieldValue?.relatedFieldId && option.relatedFieldId) {
      return option.value === fieldValue.value && option.relatedFieldId === fieldValue.relatedFieldId
    }
    return option.value === fieldValue?.value
  })(options)

export const getRiskFieldOptionLabel = (fieldValue: IRiskFieldValue, options: IFieldOption[], fieldType: FieldType) => {
  return safeGetOr<any, any, any>('', 'label')(findFieldOption(fieldValue, options, fieldType))
}

// a saved value keeps only { value, label, entity }, so the template of a linked record is restored from the options
export const getRiskFieldOptionTemplateId = (fieldValue: IRiskFieldValue, options: IFieldOption[]) => {
  const templateId = get(findFieldOption(fieldValue, options, FieldType.Lookup), 'riskTemplateId')

  return templateId ? { riskTemplateId: templateId } : {}
}

const normalizeFieldValue = (field: IField, fieldValue: any, options: Record<string, any>[]) => {
  if (field.fieldType === FieldType.Switch) {
    return fieldValue
  }

  if (!fieldValue || isEmpty(fieldValue)) {
    return
  }

  if (field.fieldType === FieldType.Tag) {
    return fieldValue.map?.((value) => ({
      label: value.title,
      value: value.title,
      description: value.description
    }))
  }

  if (field.fieldType === FieldType.Lookup) {
    if (field.multiple && Array.isArray(fieldValue)) {
      return fieldValue.map((item) => ({
        ...item,
        ...getRiskFieldOptionTemplateId(item, options as IFieldOption[]),
        label: getRiskFieldOptionLabel(item, options, field.fieldType)
      }))
    }

    return {
      ...fieldValue,
      ...getRiskFieldOptionTemplateId(fieldValue, options as IFieldOption[]),
      label: getRiskFieldOptionLabel(fieldValue, options, field.fieldType)
    }
  }

  if (field.fieldType === FieldType.PickList) {
    if (field.multiple && Array.isArray(fieldValue)) {
      return fieldValue.map((item) => ({
        label: getRiskFieldOptionLabel(item, field.options, field.fieldType),
        value: item.value
      }))
    }

    return {
      value: fieldValue.value,
      label: getRiskFieldOptionLabel(fieldValue, field.options, field.fieldType)
    }
  }

  if (field.fieldType === FieldType.Date && DATE_SPECIAL_TYPES.includes(fieldValue.fieldValue?.value)) {
    return getOptionByValue(fieldValue.fieldValue.value, DATE_OPTIONS)
  }

  return fieldValue
}

export const getFormInitialValues = async ({
  client,
  domain,
  isPublic,
  fieldValues,
  riskTemplate
}: {
  client: ApolloClient
  domain?: string
  isPublic?: boolean
  fieldValues: IFieldValueMapItem[]
  riskTemplate: IRiskTemplate
}): Promise<Record<string, any>> => {
  const fields = get(riskTemplate, 'fieldLinks', []).map(({ field, parentFieldId }) => ({ ...field, parentFieldId }))

  const fieldOptions =
    (await getFieldsOptions({
      fields,
      domain,
      client,
      isPublic,
      isRisk: true,
      fieldsValues: fieldValues
    })) ?? []

  const userOptions =
    getOptionsBySource(fields, null, [FieldSource.Users, FieldSource.Positions, FieldSource.BusinessUnits])?.options ??
    []

  const lookupOptions = [...userOptions, ...fieldOptions, ...FORM_SELECT_SECURITY_OPTIONS]

  const initialValues = fields
    .map((field) => {
      const fieldLink = riskTemplate.fieldLinks.find(
        ({ parentFieldId, field: { id } }) => id === field.id && parentFieldId === field.parentFieldId
      )

      const values: Record<string, any> = fieldValues
        .filter(({ fieldId, fieldLinkId }) => fieldId === field.id && fieldLinkId === fieldLink.id)
        .map((item) => ({
          value: normalizeFieldValue(field, item.fieldValue, lookupOptions),
          ...pick(item, ['fieldValueId', 'parentFieldValueId', 'createdAt', 'linkedRiskId', 'linkedFieldValueId'])
        }))

      if (!values.length) {
        return
      }

      const isMainForm = values.every(({ parentFieldValueId }) => !parentFieldValueId)

      if (isMainForm) {
        const value = sortBy(values, 'createdAt').map(({ value, fieldValueId, linkedRiskId, linkedFieldValueId }) => ({
          value,
          fieldValueId,
          linkedRiskId,
          linkedFieldValueId
        }))

        return [fieldLink.id, value]
      }

      return [fieldLink.id, pipe(groupBy('parentFieldValueId'), mapValues(sortByFp('createdAt')))(values)]
    })
    .filter((item) => item)

  return fromPairs(initialValues)
}

export const getFormFieldValue = (field: IField, value: any, shouldModifyDates?: boolean) => {
  let fieldValue = value

  if (field.fieldType === FieldType.Tag) {
    if (isEmpty(fieldValue)) {
      fieldValue = null
    } else {
      fieldValue = fieldValue.map(prepareTagValue)
    }
  }

  if (field.fieldType === FieldType.PickList) {
    if (field.multiple && isArray(fieldValue)) {
      if (isEmpty(fieldValue)) {
        fieldValue = null
      } else {
        fieldValue = fieldValue?.map(({ value }) => ({ value }))
      }
    } else {
      fieldValue = fieldValue?.value ? { value: fieldValue?.value } : null
    }
  }

  if (field.fieldType === FieldType.Lookup) {
    if (field.multiple && isArray(fieldValue)) {
      if (isEmpty(fieldValue)) {
        fieldValue = null
      } else {
        fieldValue = fieldValue?.map((value) => omit(value, ['label']))
      }
    } else {
      fieldValue = fieldValue?.value ? { ...omit(fieldValue, ['label']) } : null
    }
  }

  if (field.fieldType === FieldType.Switch && fieldValue?.hasOwnProperty('value')) {
    fieldValue = fieldValue!.value
  }

  if (field.fieldType === FieldType.Date && fieldValue) {
    if (DATE_SPECIAL_TYPES.includes(fieldValue.value)) {
      fieldValue = { ...omit(fieldValue, ['label']) }
    }

    if (shouldModifyDates) {
      fieldValue = {
        value: fieldValue.dueDateAt,
        offset: {
          [fieldValue.plusType.toLowerCase()]: fieldValue.plus
        }
      }
    }
  }

  if (isNil(fieldValue)) {
    fieldValue = null
  }

  return fieldValue
}

export const isLinked = (settings) => Object.values(omit(settings, '__typename')).some(Boolean)

export const filterByRiskView = (field: IField, riskView: IRiskView) => {
  if (riskView) {
    const activeFields = riskView.settings.fields.filter((item) => item.active ?? true)

    return activeFields.some(
      ({ fieldId, fieldLinkId, parentFieldId }) =>
        fieldId === field.id && (fieldLinkId ? parentFieldId === field.parentFieldId : !fieldLinkId)
    )
  }

  return field
}

export type TableCell = {
  path: string
  fieldLinkId: string
  cell: IRiskFieldValue
}

/** Table cells of the risk form with their redux-form paths: `fieldLinkId[i]` or `fieldLinkId.parentFieldValueId[i]` */
export const getTableCells = (formValues: Record<string, any>, fieldLinkIds: string[]): TableCell[] =>
  fieldLinkIds.flatMap((fieldLinkId) => {
    const value = formValues?.[fieldLinkId]

    if (!value) {
      return []
    }

    const groups: [string, IRiskFieldValue[]][] = Array.isArray(value)
      ? [[fieldLinkId, value]]
      : Object.entries(value).map(([parentFieldValueId, rows]) => [`${fieldLinkId}.${parentFieldValueId}`, rows as any])

    return groups.flatMap(([prefix, rows]) =>
      (Array.isArray(rows) ? rows : []).map((cell, idx) => ({ path: `${prefix}[${idx}]`, fieldLinkId, cell }))
    )
  })
